import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Circle, 
  ExternalLink, 
  PhoneCall, 
  Printer, 
  ArrowLeft, 
  ShieldCheck, 
  Sparkles, 
  ChevronRight,
  LifeBuoy,
  FileCheck,
  AlertTriangle
} from 'lucide-react';
import { useStore } from '../store/useStore';

export const RecoveryWizardPage: React.FC = () => {
  const { recoveryCase, toggleRecoveryStep, openAiModal } = useStore();
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const steps = recoveryCase.steps;
  const currentStep = steps[activeStepIndex] || steps[0];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 no-print">
        <div className="space-y-1">
          <Link to="/identity-theft" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Emergency Centre</span>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white">
            Identity Theft Recovery Wizard
          </h1>
          <p className="text-xs text-slate-400">
            Case Reference: <span className="font-mono text-emerald-400 font-bold">{recoveryCase.id}</span> • {recoveryCase.incidentTitle}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print Case Plan</span>
          </button>
          <button
            onClick={openAiModal}
            className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-emerald-400 text-xs font-semibold flex items-center gap-1.5 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>AI Advice</span>
          </button>
        </div>
      </div>

      {/* Progress Bar & Status (Section 13: 4 of 8 completed) */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold text-white text-sm">
            Progress: {recoveryCase.completedStepsCount} of {recoveryCase.totalStepsCount} completed
          </span>
          <span className="font-mono text-emerald-400 font-bold text-sm">
            {recoveryCase.progressPercentage}%
          </span>
        </div>
        <div className="w-full bg-slate-950 rounded-full h-3 overflow-hidden border border-slate-800">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500"
            style={{ width: `${recoveryCase.progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Interactive 8-Step Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Col: Step Navigation */}
        <div className="lg:col-span-4 space-y-2 no-print">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 mb-1">
            Recovery Milestones
          </div>
          {steps.map((step, idx) => {
            const isCurrent = idx === activeStepIndex;
            return (
              <div
                key={step.id}
                onClick={() => setActiveStepIndex(idx)}
                className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                  isCurrent
                    ? 'bg-slate-800 border-emerald-500 text-white shadow-md'
                    : 'bg-slate-900/60 border-slate-800 hover:bg-slate-800/60 text-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step.isCompleted ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-slate-400'
                  }`}>
                    {step.stepNumber}
                  </span>
                  <div className="text-xs font-semibold truncate max-w-[170px]">
                    {step.title}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRecoveryStep(step.stepNumber);
                  }}
                  className="p-1 text-slate-400 hover:text-emerald-400"
                >
                  {step.isCompleted ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <Circle className="w-5 h-5 text-slate-600" />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Right Col: Active Step Details */}
        <div className="lg:col-span-8 bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
          <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                Step {currentStep.stepNumber} of 8
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-white mt-1">
                {currentStep.title}
              </h2>
            </div>

            <button
              onClick={() => toggleRecoveryStep(currentStep.stepNumber)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors flex items-center gap-2 flex-shrink-0 ${
                currentStep.isCompleted
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700'
              }`}
            >
              {currentStep.isCompleted ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Completed</span>
                </>
              ) : (
                <>
                  <Circle className="w-4 h-4" />
                  <span>Mark as Completed</span>
                </>
              )}
            </button>
          </div>

          {/* Summary & Why It Matters */}
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 leading-relaxed font-medium">
              {currentStep.summary}
            </div>

            <div className="p-4 rounded-xl bg-amber-950/30 border border-amber-800/40 text-amber-200 space-y-1">
              <span className="font-bold text-amber-400 uppercase tracking-wider text-[10px]">Why it matters:</span>
              <p className="leading-relaxed">{currentStep.whyItMatters}</p>
            </div>
          </div>

          {/* Instructions Checklist */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              Step-by-Step Instructions:
            </h3>
            <div className="space-y-2">
              {currentStep.instructions.map((inst, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300">
                  <span className="w-5 h-5 rounded-full bg-slate-800 text-slate-400 font-bold flex items-center justify-center flex-shrink-0 text-[11px] mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="leading-relaxed">{inst}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Relevant Australian Agencies */}
          {currentStep.relevantAgencies && currentStep.relevantAgencies.length > 0 && (
            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider">
                Relevant Australian Authorities & Portals:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentStep.relevantAgencies.map((agency, i) => (
                  <div key={i} className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-2 text-xs">
                    <div className="font-bold text-white flex items-center justify-between">
                      <span>{agency.name}</span>
                      <a href={agency.website} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center gap-0.5">
                        Visit <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <p className="text-slate-400 text-[11px] leading-snug">{agency.description}</p>
                    {agency.phone && (
                      <div className="pt-1">
                        <a href={`tel:${agency.phone.replace(/\s+/g, '')}`} className="text-emerald-400 font-mono font-bold flex items-center gap-1">
                          <PhoneCall className="w-3.5 h-3.5" />
                          {agency.phone}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation between steps */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-800 no-print">
            <button
              onClick={() => setActiveStepIndex(Math.max(0, activeStepIndex - 1))}
              disabled={activeStepIndex === 0}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-200 text-xs font-semibold transition-colors"
            >
              Previous Step
            </button>

            <button
              onClick={() => setActiveStepIndex(Math.min(steps.length - 1, activeStepIndex + 1))}
              disabled={activeStepIndex === steps.length - 1}
              className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-bold transition-colors flex items-center gap-1"
            >
              <span>Next Step</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
